import type { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { CREDENTIALS_EXPIRED_LABEL } from "../../services/useCloudCredentialStore";

interface CredentialExpiredTooltipContentProps {
  reason?: string;
  side: "top" | "right" | "bottom" | "left";
}

function CredentialExpiredTooltipContent({ reason, side }: CredentialExpiredTooltipContentProps) {
  return (
    <TooltipContent
      side={side}
      align="center"
      sideOffset={2}
      className="max-w-xs overflow-visible rounded-lg border-red-200 dark:border-red-900 px-3 py-2 shadow-lg"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
        <div>
          <p className="text-sm font-semibold leading-5 text-red-600 dark:text-red-400">{CREDENTIALS_EXPIRED_LABEL}</p>
          {reason && <p className="text-xs leading-4 mt-0.5 text-muted-foreground">{reason}</p>}
        </div>
      </div>
      <TooltipPrimitive.Arrow asChild width={14} height={7}>
        <svg className="block overflow-visible" style={{ transform: "translateY(-1px)" }}>
          <polygon points="0,0 30,0 15,10" className="fill-popover" />
          <polyline
            points="0,0 15,10 30,0"
            fill="none"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            className="stroke-red-200 dark:stroke-red-900"
          />
        </svg>
      </TooltipPrimitive.Arrow>
    </TooltipContent>
  );
}

interface CloudCredentialExpiredIconProps {
  reason?: string;
  size?: "sm" | "md";
}

const ICON_SIZES = {
  sm: { frame: "w-6 h-6", icon: "w-3.5 h-3.5" },
  md: { frame: "w-8 h-8", icon: "w-4 h-4" },
};

export function CloudCredentialExpiredIcon({ reason, size = "md" }: CloudCredentialExpiredIconProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={CREDENTIALS_EXPIRED_LABEL}
            tabIndex={0}
            className={`inline-flex items-center justify-center shrink-0 rounded-full bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800 ${ICON_SIZES[size].frame}`}
            onClick={(event) => event.stopPropagation()}
            data-testid="icon-credentials-expired"
          >
            <AlertTriangle className={`${ICON_SIZES[size].icon} text-red-600 dark:text-red-400`} />
          </span>
        </TooltipTrigger>
        <CredentialExpiredTooltipContent side="top" reason={reason} />
      </Tooltip>
    </TooltipProvider>
  );
}

interface CloudCredentialExpiredSyncButtonProps {
  reason?: string;
  className?: string;
  children?: ReactNode;
  testId?: string;
}

export function CloudCredentialExpiredSyncButton({
  reason,
  className = "",
  children = "Sync",
  testId = "button-sync-credentials-expired",
}: CloudCredentialExpiredSyncButtonProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex cursor-not-allowed rounded-xl" data-testid={testId}>
            <Button
              variant="destructive"
              disabled
              aria-label={`Sync unavailable: ${CREDENTIALS_EXPIRED_LABEL}`}
              className={`flex items-center gap-2 rounded-xl font-bold bg-red-600 text-white border-red-600 dark:bg-red-700 dark:border-red-700 disabled:opacity-90 ${className}`}
            >
              <AlertTriangle className="w-4 h-4" />
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <CredentialExpiredTooltipContent side="left" reason={reason} />
      </Tooltip>
    </TooltipProvider>
  );
}

interface CloudCredentialExpiredBannerProps {
  cloudLabel: string;
  reason?: string;
  action: string;
}

export function CloudCredentialExpiredBanner({ cloudLabel, reason, action }: CloudCredentialExpiredBannerProps) {
  return (
    <div
      className="flex items-start gap-3 p-4 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 shadow-sm"
      role="alert"
      data-testid="banner-credentials-expired"
    >
      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="space-y-1">
        <p className="font-semibold">{cloudLabel} {CREDENTIALS_EXPIRED_LABEL.toLowerCase()}</p>
        <p className="text-sm">
          {reason ? `${reason}. ` : ""}Log collection and sync are paused. {action}
        </p>
      </div>
    </div>
  );
}
